import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  DynamicDrawUsage,
  InstancedBufferAttribute,
  InstancedMesh,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  OrthographicCamera,
  PlaneGeometry,
  RingGeometry,
  Scene,
  ShaderMaterial,
  Vector3,
  WebGLRenderer,
  type WebGLRendererParameters,
} from "three";

import {
  createRendererFailureChannel,
  type RendererDiagnostics,
  type RendererFailure,
  type ThreeResourceSnapshot,
  type SceneRenderer,
} from "./renderer-contract.js";
import { assertScenePlanV1, type ScenePlan, type ScenePlanSignature } from "./scene-plan.js";
import { centeredCoverageRect } from "./scene-geometry.js";
import {
  createSceneSemanticBuffer,
  PAPER_GRAIN_PATTERN,
  resizeSceneSemanticBuffer,
  writeScenePlanToSemanticBuffer,
} from "./scene-semantics.js";
import type { SceneSemanticBuffer } from "./scene-semantics.js";

interface ResizeEventTarget {
  addEventListener(type: "resize", listener: EventListener): void;
  removeEventListener(type: "resize", listener: EventListener): void;
}

export interface ThreeRendererConstructionResources {
  readonly width?: number;
  readonly height?: number;
  readonly pixelRatio?: number;
  readonly getPixelRatio?: () => number;
  readonly resizeTarget?: ResizeEventTarget;
  readonly createResizeObserver?: (callback: ResizeObserverCallback) => ResizeObserver;
  readonly createWebGLRenderer?: (parameters: WebGLRendererParameters) => WebGLRenderer;
  readonly onFailure?: (failure: RendererFailure) => void;
}

interface StagedScenePlan {
  readonly plan: ScenePlan;
  readonly signature: ScenePlanSignature;
  readonly paperColor: string;
  readonly paperGrain: number;
  readonly background: string;
  readonly washColor: string;
  readonly washOpacity: number;
  readonly washCoverage: number;
  readonly inkRed: number;
  readonly inkGreen: number;
  readonly inkBlue: number;
  readonly accentRed: number;
  readonly accentGreen: number;
  readonly accentBlue: number;
  readonly rainSlant: number;
  readonly streakCount: number;
  readonly rainX: Float32Array;
  readonly rainPhase: Float32Array;
  readonly rainSpeed: Float32Array;
  readonly rainLength: Float32Array;
  readonly rainAlpha: Float32Array;
  readonly rippleCount: number;
  readonly rippleX: Float32Array;
  readonly rippleY: Float32Array;
  readonly ripplePhase: Float32Array;
  readonly rippleRadius: Float32Array;
  readonly rippleAlpha: Float32Array;
  readonly rippleLifeMs: Float32Array;
}

interface DisposableRecord {
  readonly label: string;
  readonly dispose: () => void;
  released: boolean;
}

const MAX_STREAKS = 512;
const MAX_RIPPLES = 64;

class ThreeRendererError extends Error implements RendererFailure {
  readonly kind: RendererFailure["kind"];
  readonly phase: RendererFailure["phase"];
  readonly code: RendererFailure["code"];
  readonly recoverable: boolean;
  override readonly cause?: RendererFailure["cause"];

  constructor(failure: RendererFailure) {
    super(failure.message);
    this.name = "ThreeRendererError";
    this.kind = failure.kind;
    this.phase = failure.phase;
    this.code = failure.code;
    this.recoverable = failure.recoverable;
    this.cause = failure.cause;
    Object.freeze(this);
  }
}

function stageScenePlan(value: unknown): StagedScenePlan {
  const plan = assertScenePlanV1(value);
  const inkColor = new Color(plan.palette.ink);
  const accentColor = new Color(plan.palette.accent);
  return {
    plan: JSON.parse(JSON.stringify(plan)) as ScenePlan,
    signature: plan.signature,
    paperColor: plan.paper.color,
    paperGrain: plan.paper.grain,
    background: plan.palette.background,
    washColor: plan.wash.color,
    washOpacity: plan.wash.opacity,
    washCoverage: plan.wash.coverage,
    inkRed: inkColor.r,
    inkGreen: inkColor.g,
    inkBlue: inkColor.b,
    accentRed: accentColor.r,
    accentGreen: accentColor.g,
    accentBlue: accentColor.b,
    rainSlant: plan.rain.slant,
    streakCount: plan.rain.primitives.length,
    rainX: Float32Array.from(plan.rain.primitives, (primitive) => primitive.x),
    rainPhase: Float32Array.from(plan.rain.primitives, (primitive) => primitive.phase),
    rainSpeed: Float32Array.from(plan.rain.primitives, (primitive) => primitive.speed),
    rainLength: Float32Array.from(plan.rain.primitives, (primitive) => primitive.length),
    rainAlpha: Float32Array.from(plan.rain.primitives, (primitive) => primitive.alpha),
    rippleCount: plan.ripples.primitives.length,
    rippleX: Float32Array.from(plan.ripples.primitives, (primitive) => primitive.x),
    rippleY: Float32Array.from(plan.ripples.primitives, (primitive) => primitive.y),
    ripplePhase: Float32Array.from(plan.ripples.primitives, (primitive) => primitive.phase),
    rippleRadius: Float32Array.from(plan.ripples.primitives, (primitive) => primitive.radius),
    rippleAlpha: Float32Array.from(plan.ripples.primitives, (primitive) => primitive.alpha),
    rippleLifeMs: Float32Array.from(plan.ripples.primitives, (primitive) => primitive.lifeMs),
  };
}
function positiveDimension(value: number | undefined, fallback: number): number {
  return Number.isFinite(value) && value !== undefined && value > 0 ? value : Math.max(1, fallback);
}

function readHostDimensions(
  root: HTMLElement,
  fallbackWidth: number,
  fallbackHeight: number,
): Readonly<{ width: number; height: number }> {
  let boundsWidth: number | undefined;
  let boundsHeight: number | undefined;
  if (typeof root.getBoundingClientRect === "function") {
    try {
      const bounds = root.getBoundingClientRect();
      boundsWidth = bounds.width;
      boundsHeight = bounds.height;
    } catch {
      // Fall through to client and boundary/config dimensions.
    }
  }
  return {
    width: positiveDimension(boundsWidth, positiveDimension(root.clientWidth, fallbackWidth)),
    height: positiveDimension(boundsHeight, positiveDimension(root.clientHeight, fallbackHeight)),
  };
}

function positivePixelRatio(value: number): number {
  return Number.isFinite(value) && value > 0 ? Math.min(2, value) : 1;
}

export function createThreeSceneRenderer(
  root: HTMLElement,
  resources: ThreeRendererConstructionResources,
  initialScenePlan: ScenePlan,
): SceneRenderer {
  const failures = createRendererFailureChannel();
  if (resources.onFailure !== undefined) failures.onFailure(resources.onFailure);

  let initialCandidate: StagedScenePlan;
  try {
    initialCandidate = stageScenePlan(initialScenePlan);
  } catch (cause) {
    failures.emit({
      kind: "three",
      phase: "initialize",
      code: "apply",
      message: "Three renderer rejected its initial ScenePlan",
      recoverable: false,
      cause,
    });
    const failure = failures.lastFailure();
    failures.destroy();
    if (failure !== null) throw new ThreeRendererError(failure);
    throw new Error("Three renderer rejected its initial ScenePlan", { cause });
  }

  const records: DisposableRecord[] = [];
  let webgl: WebGLRenderer | null = null;
  let canvas: HTMLCanvasElement | null = null;
  let scene: Scene | null = null;
  let contextLossHandler: EventListener | null = null;
  let resizeHandler: EventListener | null = null;
  let resizeObserver: ResizeObserver | null = null;
  let resizeTarget: ResizeEventTarget | null = null;
  let contextSubscribed = false;
  let resizeSubscribed = false;
  let observerSubscribed = false;
  let canvasAttached = false;
  let rootPositionChanged = false;
  let rootIsolationChanged = false;
  let rootPositionRestored = true;
  let rootIsolationRestored = true;
  let previousRootPosition = "";
  let previousRootIsolation = "";

  const register = (label: string, disposable: { dispose(): void }): void => {
    records.push({ label, dispose: (): void => disposable.dispose(), released: false });
  };

  try {
    const initialBounds = readHostDimensions(
      root,
      positiveDimension(resources.width, 1),
      positiveDimension(resources.height, 1),
    );
    let width = initialBounds.width;
    let height = initialBounds.height;
    const defaultWindow = typeof window === "undefined" ? null : window;
    const getPixelRatio = resources.getPixelRatio
      ?? ((): number => resources.pixelRatio ?? defaultWindow?.devicePixelRatio ?? 1);
    let pixelRatio = positivePixelRatio(getPixelRatio());
    resizeTarget = resources.resizeTarget ?? defaultWindow;
    const createWebGLRenderer = resources.createWebGLRenderer
      ?? ((parameters: WebGLRendererParameters): WebGLRenderer => new WebGLRenderer(parameters));
    webgl = createWebGLRenderer({
      alpha: true,
      antialias: true,
      powerPreference: "low-power",
    });
    register("renderer", webgl);
    canvas = webgl.domElement;

    previousRootPosition = root.style.position;
    previousRootIsolation = root.style.isolation;
    const computedPosition = typeof getComputedStyle === "function"
      ? getComputedStyle(root).position
      : root.style.position;
    if (computedPosition === "" || computedPosition === "static") {
      root.style.position = "relative";
      rootPositionChanged = true;
      rootPositionRestored = false;
    }
    if (root.style.isolation !== "isolate") {
      root.style.isolation = "isolate";
      rootIsolationChanged = true;
      rootIsolationRestored = false;
    }

    canvas.className = "north-star-three-canvas";
    canvas.setAttribute("aria-hidden", "true");
    canvas.setAttribute("role", "presentation");
    canvas.setAttribute("data-testid", "experience-canvas");
    canvas.setAttribute("data-north-star-renderer", "three");
    canvas.style.display = "block";
    canvas.style.position = "absolute";
    canvas.style.inset = "0px";
    canvas.style.minWidth = "100%";
    canvas.style.minHeight = "100%";
    canvas.style.pointerEvents = "none";
    canvas.style.zIndex = "-1";
    root.appendChild(canvas);
    canvasAttached = true;

    scene = new Scene();
    const camera = new OrthographicCamera(-width / 2, width / 2, height / 2, -height / 2, 0.1, 20);
    camera.position.z = 10;

    const paperGeometry = new PlaneGeometry(2, 2);
    register("paper geometry", paperGeometry);
    const paperUniforms = {
      paperColor: { value: new Color("#ffffff") },
      paperGrainColor: { value: new Color("#000000") },
      paperGrainOpacity: { value: 0 },
      paperGrainScale: { value: 8 },
      paperGrainRowHeight: { value: PAPER_GRAIN_PATTERN.rowHeightPx },
      paperPixelRatio: { value: pixelRatio },
      paperViewportHeight: { value: height },
    };
    const paperMaterial = new ShaderMaterial({
      depthWrite: false,
      transparent: false,
      uniforms: paperUniforms,
      vertexShader: `
        void main() {
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 paperColor;
        uniform vec3 paperGrainColor;
        uniform float paperGrainOpacity;
        uniform float paperGrainScale;
        uniform float paperGrainRowHeight;
        uniform float paperPixelRatio;
        uniform float paperViewportHeight;
        void main() {
          float cssY = paperViewportHeight - gl_FragCoord.y / paperPixelRatio;
          float paperFiber = 1.0 - step(
            paperGrainRowHeight,
            mod(cssY, paperGrainScale)
          );
          vec3 color = mix(paperColor, paperGrainColor, paperFiber * paperGrainOpacity);
          gl_FragColor = vec4(color, 1.0);
        }
      `,
    });
    register("paper material", paperMaterial);
    const paper = new Mesh(paperGeometry, paperMaterial);
    paper.position.z = -3;

    const washGeometry = new PlaneGeometry(2, 2);
    register("wash geometry", washGeometry);
    const washMaterial = new MeshBasicMaterial({ depthWrite: false, transparent: true });
    register("wash material", washMaterial);
    const wash = new Mesh(washGeometry, washMaterial);
    wash.position.z = -2;

    const rainPositions = new Float32Array(MAX_STREAKS * 6);
    const rainColors = new Float32Array(MAX_STREAKS * 8);
    const rainPositionAttribute = new BufferAttribute(rainPositions, 3);
    rainPositionAttribute.setUsage(DynamicDrawUsage);
    const rainColorAttribute = new BufferAttribute(rainColors, 4);
    rainColorAttribute.setUsage(DynamicDrawUsage);
    const rainGeometry = new BufferGeometry();
    rainGeometry.setAttribute("position", rainPositionAttribute);
    rainGeometry.setAttribute("color", rainColorAttribute);
    rainGeometry.setDrawRange(0, 0);
    register("rain geometry", rainGeometry);
    const rainMaterial = new LineBasicMaterial({
      depthWrite: false,
      transparent: true,
      vertexColors: true,
    });
    register("rain material", rainMaterial);
    const rain = new LineSegments(rainGeometry, rainMaterial);

    const rippleAlpha = new Float32Array(MAX_RIPPLES);
    const rippleAlphaAttribute = new InstancedBufferAttribute(rippleAlpha, 1);
    rippleAlphaAttribute.setUsage(DynamicDrawUsage);
    const rippleGeometry = new RingGeometry(0.86, 1, 32);
    rippleGeometry.setAttribute("instanceAlpha", rippleAlphaAttribute);
    register("ripple geometry", rippleGeometry);
    const rippleColor = new Color();
    const rippleMaterial = new ShaderMaterial({
      depthWrite: false,
      side: DoubleSide,
      transparent: true,
      uniforms: { diffuse: { value: rippleColor } },
      vertexShader: `
        attribute float instanceAlpha;
        varying float vAlpha;
        void main() {
          vAlpha = instanceAlpha;
          gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 diffuse;
        varying float vAlpha;
        void main() {
          gl_FragColor = vec4(diffuse, vAlpha);
        }
      `,
    });
    register("ripple material", rippleMaterial);
    const ripples = new InstancedMesh(rippleGeometry, rippleMaterial, MAX_RIPPLES);
    ripples.instanceMatrix.setUsage(DynamicDrawUsage);
    ripples.count = 0;
    ripples.position.z = 1;
    register("ripple instance buffer", ripples);
    const rippleMatrix = new Matrix4();
    const projectionPoint = new Vector3();

    scene.add(paper, wash, rain, ripples);
    const semanticBounds = { width, height, pixelRatio };
    const semanticBuffer = createSceneSemanticBuffer(initialCandidate.plan);

    let appliedCandidate: StagedScenePlan | null = null;
    let streakCount = 0;
    let rippleCount = 0;
    let washCoverage = 0;
    let lastFrameMs = 0;
    let contextLost = false;
    let poisoned = false;
    let destroyRequested = false;
    let destroyComplete = false;
    let renderState: ThreeResourceSnapshot | null = null;
    let observationDirty = true;
    let cachedDiagnostics: RendererDiagnostics | null = null;
    let observationBuildCount = 0;
    let applyCount = 0;
    let frameCount = 0;
    let renderCount = 0;
    let releaseAttempts = 0;
    let releaseFailures = 0;

    const markDiagnosticsDirty = (renderResourcesChanged = false): void => {
      cachedDiagnostics = null;
      if (renderResourcesChanged) observationDirty = true;
    };

    const writeRainState = (state: SceneSemanticBuffer): void => {
      for (let index = 0; index < streakCount; index += 1) {
        const segment = state.rain[index];
        const offset = index * 6;
        rainPositions[offset] = segment.startX - width / 2;
        rainPositions[offset + 1] = height / 2 - segment.startY;
        rainPositions[offset + 2] = 0;
        rainPositions[offset + 3] = segment.endX - width / 2;
        rainPositions[offset + 4] = height / 2 - segment.endY;
        rainPositions[offset + 5] = 0;
      }
      rainPositionAttribute.needsUpdate = true;
    };

    const writeRippleState = (state: SceneSemanticBuffer): void => {
      for (let index = 0; index < rippleCount; index += 1) {
        const rippleState = state.ripples[index];
        rippleMatrix.makeScale(rippleState.radius, rippleState.radius, 1);
        rippleMatrix.setPosition(
          rippleState.centerX - width / 2,
          height / 2 - rippleState.centerY,
          0,
        );
        ripples.setMatrixAt(index, rippleMatrix);
        rippleAlpha[index] = rippleState.alpha;
      }
      ripples.instanceMatrix.needsUpdate = true;
      rippleAlphaAttribute.needsUpdate = true;
    };
    const observeResources = (): ThreeResourceSnapshot => {
      camera.updateMatrixWorld(true);
      scene?.updateMatrixWorld(true);
      const observedWidth = camera.right - camera.left;
      const observedHeight = camera.top - camera.bottom;
      const cssWidth = Number.parseFloat(canvas?.style.width ?? "");
      const backingWidth = canvas?.width ?? 0;
      const rendererRatio = typeof webgl?.getPixelRatio === "function"
        ? webgl.getPixelRatio()
        : backingWidth / cssWidth;
      const observedPixelRatio = positivePixelRatio(rendererRatio);
      const projectToViewport = (
        x: number,
        y: number,
        z: number,
        matrixWorld: Matrix4,
      ): Readonly<{ x: number; y: number }> => {
        projectionPoint.set(x, y, z).applyMatrix4(matrixWorld).project(camera);
        return Object.freeze({
          x: (projectionPoint.x + 1) * observedWidth / 2,
          y: (1 - projectionPoint.y) * observedHeight / 2,
        });
      };
      const washWidth = wash.scale.x * 2;
      const washHeight = wash.scale.y * 2;
      const observedRainCount = Math.floor(rainGeometry.drawRange.count / 2);
      const observedRain = Array.from({ length: observedRainCount }, (_, index) => {
        const offset = index * 6;
        const colorOffset = index * 8;
        const color = new Color().setRGB(
          rainColors[colorOffset],
          rainColors[colorOffset + 1],
          rainColors[colorOffset + 2],
        );
        const start = projectToViewport(
          rainPositions[offset],
          rainPositions[offset + 1],
          rainPositions[offset + 2],
          rain.matrixWorld,
        );
        const end = projectToViewport(
          rainPositions[offset + 3],
          rainPositions[offset + 4],
          rainPositions[offset + 5],
          rain.matrixWorld,
        );
        return Object.freeze({
          startX: start.x,
          startY: start.y,
          endX: end.x,
          endY: end.y,
          color: `#${color.getHexString()}`,
          alpha: rainColors[colorOffset + 3],
        });
      });
      const observedRippleColor = rippleMaterial.uniforms.diffuse?.value as Color;
      const observedRipples = Array.from({ length: ripples.count }, (_, index) => {
        ripples.getMatrixAt(index, rippleMatrix);
        const elements = rippleMatrix.elements;
        const radius = Math.hypot(elements[0], elements[1], elements[2]);
        const center = projectToViewport(
          elements[12],
          elements[13],
          elements[14],
          ripples.matrixWorld,
        );
        return Object.freeze({
          centerX: center.x,
          centerY: center.y,
          radius,
          color: `#${observedRippleColor.getHexString()}`,
          alpha: rippleAlpha[index],
        });
      });
      return Object.freeze({
        kind: "three-resources",
        bounds: Object.freeze({
          width: observedWidth,
          height: observedHeight,
          pixelRatio: observedPixelRatio,
        }),
        paper: Object.freeze({
          color: `#${paperUniforms.paperColor.value.getHexString()}`,
          grainColor: `#${paperUniforms.paperGrainColor.value.getHexString()}`,
          grainOpacity: paperUniforms.paperGrainOpacity.value,
          grainScalePx: paperUniforms.paperGrainScale.value,
        }),
        wash: Object.freeze({
          color: `#${washMaterial.color.getHexString()}`,
          opacity: washMaterial.opacity,
          coverage: washWidth / observedWidth,
          rect: Object.freeze({
            x: wash.position.x - wash.scale.x - camera.left,
            y: camera.top - wash.position.y - wash.scale.y,
            width: washWidth,
            height: washHeight,
          }),
        }),
        rain: Object.freeze(observedRain),
        ripples: Object.freeze(observedRipples),
      });
    };
    const writeFrameResources = (nowMs: number): void => {
      if (appliedCandidate === null) return;
      semanticBounds.width = width;
      semanticBounds.height = height;
      semanticBounds.pixelRatio = pixelRatio;
      const nextState = writeScenePlanToSemanticBuffer(
        semanticBuffer,
        appliedCandidate.plan,
        semanticBounds,
        nowMs,
      );
      writeRainState(nextState);
      writeRippleState(nextState);
    };

    const updateWashScale = (): void => {
      paper.scale.set(width / 2, height / 2, 1);
      const coverage = centeredCoverageRect(Math.max(0.01, washCoverage), width, height);
      wash.scale.set(
        coverage.width / 2,
        coverage.height / 2,
        1,
      );
    };

    const commitCandidate = (candidate: StagedScenePlan): void => {
      webgl?.setClearColor(candidate.background, 1);
      paperUniforms.paperColor.value.set(candidate.paperColor);
      washMaterial.color.set(candidate.washColor);
      washMaterial.opacity = candidate.washOpacity;
      washCoverage = candidate.washCoverage;
      updateWashScale();
      resizeSceneSemanticBuffer(semanticBuffer, candidate.plan);
      semanticBounds.width = width;
      semanticBounds.height = height;
      semanticBounds.pixelRatio = pixelRatio;
      const nextRenderState = writeScenePlanToSemanticBuffer(
        semanticBuffer,
        candidate.plan,
        semanticBounds,
        lastFrameMs,
      );
      paperUniforms.paperGrainColor.value.set(nextRenderState.paper.grainColor);
      paperUniforms.paperGrainOpacity.value = nextRenderState.paper.grainOpacity;
      paperUniforms.paperGrainScale.value = nextRenderState.paper.grainScalePx;
      paperUniforms.paperGrainRowHeight.value = nextRenderState.paper.pattern.rowHeightPx;
      paperUniforms.paperPixelRatio.value = pixelRatio;
      paperUniforms.paperViewportHeight.value = height;

      streakCount = candidate.streakCount;
      for (let index = 0; index < streakCount; index += 1) {
        const colorOffset = index * 8;
        rainColors[colorOffset] = candidate.inkRed;
        rainColors[colorOffset + 1] = candidate.inkGreen;
        rainColors[colorOffset + 2] = candidate.inkBlue;
        rainColors[colorOffset + 3] = candidate.rainAlpha[index];
        rainColors[colorOffset + 4] = candidate.inkRed;
        rainColors[colorOffset + 5] = candidate.inkGreen;
        rainColors[colorOffset + 6] = candidate.inkBlue;
        rainColors[colorOffset + 7] = candidate.rainAlpha[index];
      }
      rain.visible = streakCount > 0;
      rainGeometry.setDrawRange(0, streakCount * 2);
      rainColorAttribute.needsUpdate = true;

      rippleCount = candidate.rippleCount;
      rippleColor.setRGB(candidate.accentRed, candidate.accentGreen, candidate.accentBlue);
      for (let index = 0; index < rippleCount; index += 1) {
        rippleAlpha[index] = candidate.rippleAlpha[index];
      }
      rippleAlphaAttribute.needsUpdate = true;
      ripples.count = rippleCount;
      ripples.visible = rippleCount > 0;
      writeRainState(nextRenderState);
      writeRippleState(nextRenderState);
      markDiagnosticsDirty(true);
    };

    const emitAndThrow = (
      input: Parameters<typeof failures.emit>[0],
    ): never => {
      markDiagnosticsDirty();
      failures.emit(input);
      const failure = failures.lastFailure();
      if (failure !== null) throw new ThreeRendererError(failure);
      throw new Error(input.message);
    };

    const apply = (nextScenePlan: ScenePlan): void => {
      if (destroyRequested || poisoned) return;
      let candidate: StagedScenePlan;
      try {
        candidate = stageScenePlan(nextScenePlan);
      } catch (cause) {
        return emitAndThrow({
          kind: "three",
          phase: "apply",
          code: "apply",
          message: "Three renderer rejected ScenePlan",
          recoverable: false,
          cause,
        });
      }
      const previous = appliedCandidate;
      try {
        commitCandidate(candidate);
        appliedCandidate = candidate;
        applyCount += 1;
      } catch (cause) {
        markDiagnosticsDirty(true);
        if (previous !== null) {
          try {
            commitCandidate(previous);
          } catch (rollbackCause) {
            poisoned = true;
            markDiagnosticsDirty();
            failures.emit({
              kind: "three",
              phase: "apply",
              code: "resource",
              message: "Three renderer failed to restore the prior render state",
              recoverable: false,
              cause: rollbackCause,
            });
            const failure = failures.lastFailure();
            if (failure !== null) throw new ThreeRendererError(failure);
            throw new Error("Three renderer failed to restore the prior render state", {
              cause: rollbackCause,
            });
          }
        }
        return emitAndThrow({
          kind: "three",
          phase: "apply",
          code: "apply",
          message: "Three renderer failed to apply ScenePlan atomically",
          recoverable: true,
          cause,
        });
      }
    };

    const updateViewport = (): void => {
      const hostBounds = readHostDimensions(root, width, height);
      const nextWidth = hostBounds.width;
      const nextHeight = hostBounds.height;
      const nextPixelRatio = positivePixelRatio(getPixelRatio());
      width = nextWidth;
      height = nextHeight;
      pixelRatio = nextPixelRatio;
      paperUniforms.paperPixelRatio.value = pixelRatio;
      paperUniforms.paperViewportHeight.value = height;
      webgl?.setPixelRatio(pixelRatio);
      webgl?.setSize(width, height, false);
      if (canvas !== null) {
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
      }
      camera.left = -width / 2;
      camera.right = width / 2;
      camera.top = height / 2;
      camera.bottom = -height / 2;
      camera.updateProjectionMatrix();
      updateWashScale();
      if (appliedCandidate !== null) {
        writeFrameResources(lastFrameMs);
        markDiagnosticsDirty(true);
      }
    };

    const updateViewportSafely = (): void => {
      if (destroyRequested || poisoned) return;
      try {
        updateViewport();
      } catch (cause) {
        markDiagnosticsDirty(true);
        failures.emit({
          kind: "three",
          phase: "frame",
          code: "resource",
          message: "Three renderer failed to update viewport resources",
          recoverable: true,
          cause,
        });
      }
    };

    const frame = (nowMs: number): void => {
      if (destroyRequested || contextLost || poisoned) return;
      frameCount += 1;
      markDiagnosticsDirty(true);
      try {
        lastFrameMs = nowMs;
        writeFrameResources(nowMs);
        wash.position.x = 0;
        webgl?.render(scene as Scene, camera);
        renderCount += 1;
        markDiagnosticsDirty();
      } catch (cause) {
        markDiagnosticsDirty();
        failures.emit({
          kind: "three",
          phase: "frame",
          code: "frame",
          message: "Three renderer failed to render a frame",
          recoverable: true,
          cause,
        });
      }
    };

    contextLossHandler = (event: Event): void => {
      event.preventDefault();
      if (destroyRequested || contextLost) return;
      contextLost = true;
      markDiagnosticsDirty();
      failures.emit({
        kind: "three",
        phase: "frame",
        code: "context-lost",
        message: "Three renderer WebGL context was lost",
        recoverable: true,
      });
    };
    contextSubscribed = true;
    canvas.addEventListener("webglcontextlost", contextLossHandler);

    resizeHandler = (): void => updateViewportSafely();
    if (resizeTarget !== null) {
      resizeSubscribed = true;
      resizeTarget.addEventListener("resize", resizeHandler);
    }
    const observerFactory = resources.createResizeObserver
      ?? (typeof ResizeObserver === "undefined"
        ? undefined
        : (callback: ResizeObserverCallback): ResizeObserver => new ResizeObserver(callback));
    if (observerFactory !== undefined) {
      resizeObserver = observerFactory((): void => updateViewportSafely());
      observerSubscribed = true;
      resizeObserver.observe(root);
    }

    updateViewport();
    commitCandidate(initialCandidate);
    appliedCandidate = initialCandidate;
    applyCount = 1;

    const activeOwnedSubscriptions = (): number =>
      Number(contextSubscribed) + Number(resizeSubscribed) + Number(observerSubscribed);
    const unreleasedObjectCount = (): number => {
      let count = 0;
      for (let index = 0; index < records.length; index += 1) {
        if (!records[index].released) count += 1;
      }
      return count;
    };
    const diagnostics = (): RendererDiagnostics => {
      if (cachedDiagnostics !== null) return cachedDiagnostics;
      const canvasCount = canvasAttached && canvas?.parentNode === root ? 1 : 0;
      const objectCount = destroyComplete ? 0 : unreleasedObjectCount();
      const activeSubscriptions = failures.activeSubscriptions() + activeOwnedSubscriptions();
      if (!destroyComplete && observationDirty) {
        renderState = observeResources();
        observationBuildCount += 1;
        observationDirty = false;
      }
      cachedDiagnostics = Object.freeze({
        kind: "three",
        signature: destroyComplete || poisoned ? null : appliedCandidate?.signature ?? null,
        framePolicy: "host-raf",
        cleanupComplete: destroyComplete,
        canvasCount,
        objectCount,
        activeSubscriptions,
        allocationsAfterApply: 0,
        lastFailure: failures.lastFailure(),
        observation: Object.freeze({
          kind: "three",
          applyCount,
          frameCount,
          renderCount,
          observationBuildCount,
          renderState: destroyComplete ? null : renderState,
          viewport: Object.freeze({ width, height, pixelRatio }),
          resources: Object.freeze({
            canvasCount,
            objectCount,
            activeSubscriptions,
            releaseAttempts,
            releaseFailures,
          }),
        }),
      });
      return cachedDiagnostics;
    };

    const emitCleanupFailure = (label: string, cause: unknown): void => {
      markDiagnosticsDirty();
      failures.emit({
        kind: "three",
        phase: "destroy",
        code: "cleanup",
        message: `Three renderer failed to release ${label}`,
        recoverable: true,
        cause,
      });
    };
    const destroy = (): void => {
      if (destroyComplete) return;
      destroyRequested = true;
      markDiagnosticsDirty();

      if (contextSubscribed && canvas !== null && contextLossHandler !== null) {
        releaseAttempts += 1;
        try {
          canvas.removeEventListener("webglcontextlost", contextLossHandler);
          contextSubscribed = false;
        } catch (cause) {
          releaseFailures += 1;
          emitCleanupFailure("context-loss listener", cause);
        }
      }
      if (resizeSubscribed && resizeTarget !== null && resizeHandler !== null) {
        releaseAttempts += 1;
        try {
          resizeTarget.removeEventListener("resize", resizeHandler);
          resizeSubscribed = false;
        } catch (cause) {
          releaseFailures += 1;
          emitCleanupFailure("DPR listener", cause);
        }
      }
      if (observerSubscribed && resizeObserver !== null) {
        releaseAttempts += 1;
        try {
          resizeObserver.disconnect();
          observerSubscribed = false;
        } catch (cause) {
          releaseFailures += 1;
          emitCleanupFailure("resize observer", cause);
        }
      }
      for (let index = records.length - 1; index >= 0; index -= 1) {
        const record = records[index];
        if (record.released) continue;
        releaseAttempts += 1;
        try {
          record.dispose();
          record.released = true;
        } catch (cause) {
          releaseFailures += 1;
          emitCleanupFailure(record.label, cause);
        }
      }
      if (canvasAttached && canvas !== null) {
        releaseAttempts += 1;
        try {
          const parent = canvas.parentNode;
          if (parent !== null) parent.removeChild(canvas);
          if (canvas.parentNode !== null) {
            throw new Error("Three renderer canvas remained attached after removal");
          }
          canvasAttached = false;
        } catch (cause) {
          releaseFailures += 1;
          emitCleanupFailure("canvas", cause);
        }
      }
      if (!rootPositionRestored && rootPositionChanged) {
        releaseAttempts += 1;
        try {
          root.style.position = previousRootPosition;
          rootPositionRestored = true;
        } catch (cause) {
          releaseFailures += 1;
          emitCleanupFailure("host position", cause);
        }
      }
      if (!rootIsolationRestored && rootIsolationChanged) {
        releaseAttempts += 1;
        try {
          root.style.isolation = previousRootIsolation;
          rootIsolationRestored = true;
        } catch (cause) {
          releaseFailures += 1;
          emitCleanupFailure("host isolation", cause);
        }
      }

      const allReleased = records.every((record) => record.released);
      if (
        allReleased && !contextSubscribed && !resizeSubscribed && !observerSubscribed
        && !canvasAttached && (canvas === null || canvas.parentNode === null)
        && rootPositionRestored && rootIsolationRestored
      ) {
        scene?.clear();
        appliedCandidate = null;
        renderState = null;
        destroyComplete = true;
        failures.destroy();
      }
      markDiagnosticsDirty(true);
    };

    const onFailure = (listener: (failure: RendererFailure) => void): (() => void) => {
      const unsubscribe = failures.onFailure(listener);
      let subscribed = true;
      markDiagnosticsDirty();
      return (): void => {
        if (!subscribed) return;
        subscribed = false;
        unsubscribe();
        markDiagnosticsDirty();
      };
    };

    return {
      kind: "three",
      apply,
      frame,
      onFailure,
      diagnostics,
      destroy,
    };
  } catch (cause) {
    let rollbackCause: unknown;
    const attempt = (operation: () => void, onSuccess?: () => void): void => {
      try {
        operation();
        onSuccess?.();
      } catch (cleanupCause) {
        rollbackCause ??= cleanupCause;
      }
    };
    if (contextSubscribed && canvas !== null && contextLossHandler !== null) {
      attempt(
        () => canvas?.removeEventListener("webglcontextlost", contextLossHandler as EventListener),
        () => { contextSubscribed = false; },
      );
    }
    if (resizeSubscribed && resizeTarget !== null && resizeHandler !== null) {
      attempt(
        () => resizeTarget?.removeEventListener("resize", resizeHandler as EventListener),
        () => { resizeSubscribed = false; },
      );
    }
    if (observerSubscribed && resizeObserver !== null) {
      attempt(() => resizeObserver?.disconnect(), () => { observerSubscribed = false; });
    }
    for (let index = records.length - 1; index >= 0; index -= 1) {
      if (!records[index].released) {
        attempt(records[index].dispose, () => { records[index].released = true; });
      }
    }
    if (canvasAttached && canvas !== null && canvas.parentNode === root) {
      attempt(() => root.removeChild(canvas as HTMLCanvasElement), () => { canvasAttached = false; });
    }
    if (rootPositionChanged) {
      attempt(() => { root.style.position = previousRootPosition; }, () => { rootPositionRestored = true; });
    }
    if (rootIsolationChanged) {
      attempt(() => { root.style.isolation = previousRootIsolation; }, () => { rootIsolationRestored = true; });
    }
    const rollbackComplete = !contextSubscribed && !resizeSubscribed && !observerSubscribed
      && records.every((record) => record.released) && !canvasAttached
      && rootPositionRestored && rootIsolationRestored;
    if (!rollbackComplete || rollbackCause !== undefined) {
      failures.emit({
        kind: "three",
        phase: "initialize",
        code: "resource",
        message: "Three renderer construction rollback was incomplete",
        recoverable: false,
        cause: rollbackCause,
      });
      const failure = failures.lastFailure();
      failures.destroy();
      if (failure !== null) throw new ThreeRendererError(failure);
      throw new Error("Three renderer construction rollback was incomplete", { cause: rollbackCause });
    }
    failures.emit({
      kind: "three",
      phase: "initialize",
      code: "initialization",
      message: "Three renderer WebGL initialization failed",
      recoverable: true,
      cause,
    });
    const failure = failures.lastFailure();
    failures.destroy();
    if (failure !== null) throw new ThreeRendererError(failure);
    throw new Error("Three renderer WebGL initialization failed", { cause });
  }
}

export const createThreeRenderer = createThreeSceneRenderer;
